import {
  FormControl,
  Radio,
  RadioGroup,
  Typography,
  Divider,
  ListItemButton,
  FormControlLabel,
  styled,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { SettingsSection } from '$common/settings-section'
import { closeUserSessionsLimit } from '$logic/router'
import { useUserSessionsLimit } from '$logic/hall/react-hook'
import { nothing } from '$func/utils'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { setHallUserSessionsLimit } from '$logic/hall/hall.service'
import { t } from '$i18n'

// Styled components
const StyledFormControlLabel = styled(FormControlLabel)`
  width: 100%;
  height: 100%;

  margin: 0;
  justify-content: space-between;
`

const StyledListItemButton = styled(ListItemButton)`
  height: 50px;
  padding: 0 20px;
`

const IntendedDivider = styled(Divider)`
  margin-right: 20px;
`

export function UserSessionsLimit({ roomId }: { roomId: string }) {
  const { value: hallSetLimit } = useUserSessionsLimit(roomId)

  const [limit, setLimit] = useState<number>(0)

  useEffect(() => {
    setLimit(hallSetLimit ?? 0)
  }, [hallSetLimit])

  const onConfirm = async () => {
    if (hallSetLimit !== limit) {
      await showSaveChangesLoadingModal()

      const succeed = await setHallUserSessionsLimit(roomId, limit)
      await closeCurrentModal()

      if (succeed) {
        window.analite('PROFILE_SETTING_SESSION_LIMIT', { limit })
        await closeUserSessionsLimit()
      }
    }
  }

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={t('userSessionsLimit.title')}
          onBack={closeUserSessionsLimit}
          onConfirm={onConfirm}
          confirmDisabled={hallSetLimit === limit}
        />
      }
    >
      <SettingsSection headerText={t('userSessionsLimit.description')}>
        <FormControl component="fieldset" fullWidth onChange={nothing}>
          <RadioGroup
            name="sessions-limit-radio-group"
            value={limit}
            onChange={event => setLimit(parseInt(event.target.value))}
          >
            <StyledListItemButton>
              <StyledFormControlLabel
                value={1}
                control={<Radio />}
                disableTypography
                label={
                  <Typography variant="body2">
                    {t('userSessionsLimit.one')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>

            <IntendedDivider />

            <StyledListItemButton>
              <StyledFormControlLabel
                value={2}
                control={<Radio />}
                disableTypography
                label={
                  <Typography variant="body2">
                    {t('userSessionsLimit.two')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>

            <IntendedDivider />

            <StyledListItemButton>
              <StyledFormControlLabel
                value={3}
                control={<Radio />}
                disableTypography
                label={
                  <Typography variant="body2">
                    {t('userSessionsLimit.three')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>

            <IntendedDivider />

            <StyledListItemButton>
              <StyledFormControlLabel
                value={0}
                disableTypography
                control={<Radio />}
                label={
                  <Typography variant="body2">
                    {t('userSessionsLimit.unlimited')}
                  </Typography>
                }
                labelPlacement="start"
              />
            </StyledListItemButton>
          </RadioGroup>
        </FormControl>
      </SettingsSection>
    </PageWithAppBar>
  )
}
